import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 * @property {HTMLElement[]} headerLabels – The header label elements per fieldset.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Element | null} */
  #quickAddDialog = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** Bound once to prevent duplicate listeners if connectedCallback re-fires after morph */
  #variantChangedBound = this.variantChanged.bind(this);

  /** Incremented per fetch to prevent stale re-enables from aborted requests */
  #fetchGeneration = 0;

  connectedCallback() {
    super.connectedCallback();

    // Reset before populating — section morph can disconnect/reconnect the same instance,
    // causing push() to accumulate stale entries and breaking #restoreSelectionState.
    this.#radios = [];
    this.#checkedIndices = [];

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    });

    this.addEventListener('change', this.#variantChangedBound);
    this.addEventListener('click', this.#handleDropdownBackdropClick);
    this.addEventListener('toggle', this.#handleDropdownToggle, true);
    this.#resizeObserver.observe(this);

    const quickAddDialog = this.closest('quick-add-dialog');
    if (quickAddDialog) {
      this.#quickAddDialog = quickAddDialog;
      quickAddDialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    }
  }

  /** Closes any open variant dropdown when the quick-add modal closes. */
  #handleDialogClose = () => {
    for (const details of this.querySelectorAll('details.variant-option__dropdown-details[open]')) {
      details.open = false;
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    if (this.#quickAddDialog) {
      this.#quickAddDialog.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
      this.#quickAddDialog = null;
    }
    document.documentElement.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('padding-right');
  }

  /**
   * Aborts any in-flight Section Rendering API fetch.
   * Called by sibling-products before a section-level morph to prevent
   * a stale response from overwriting the newly morphed product's HTML.
   */
  abortPendingFetches() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = undefined;
    }
    this.#pendingRequestUrl = undefined;
  }

  /**
   * Writes the live checked state onto the checked attribute so it survives caching.
   * @param {boolean} [clearLiveState] - Also clear the property; pass true only when this product's DOM is about to be replaced by a morph.
   */
  persistSelectionToAttributes(clearLiveState = false) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    for (const fieldset of fieldsets) {
      for (const radio of fieldset.querySelectorAll('input')) {
        if (radio.checked) {
          radio.setAttribute('checked', '');
        } else {
          radio.removeAttribute('checked');
        }
        if (clearLiveState) {
          radio.checked = false;
        }
      }
    }
  }

  /**
   * Closes the dropdown panel containing the clicked close button.
   * @param {Event} event
   */
  handleDropdownClose = (event) => {
    const details = event.target?.closest('details.variant-option__dropdown-details');
    if (details instanceof HTMLDetailsElement) details.open = false;
  };

  /**
   * Closes the dropdown panel then opens the size guide drawer.
   * Closing the details first ensures the scroll lock added by the dropdown toggle
   * is cleared before the drawer's own scroll lock is applied.
   * @param {Event} event
   */
  handleSizeChartClick = (event) => {
    const details = event.target?.closest('details.variant-option__dropdown-details');
    if (details instanceof HTMLDetailsElement) details.open = false;
    const drawer = document.querySelector('size-guide-drawer');
    drawer?.open();
  };

  /**
   * Rebuilds internal radio and selection state from the live DOM after a morph.
   * Called via queueMicrotask by the morph onAfterUpdate hook.
   */
  updatedCallback() {
    super.updatedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'))
    );
    this.#radios = fieldsets.map((fs) => Array.from(fs.querySelectorAll('input')));
    this.#checkedIndices = this.#radios.map((radios, i) => {
      const indices = [];
      // Use the checked DOM property as source of truth — immune to onBeforeUpdate
      // ghost copies that can set data-current-checked inconsistently after section morphs.
      const currentIndex = radios.findIndex((r) => r.checked);
      if (currentIndex !== -1) {
        indices.push(currentIndex);
        radios[currentIndex].dataset.currentChecked = 'true';
      }
      // Trust data-previous-checked for pill animation continuity only
      const previousIndex = radios.findIndex((r) => r.dataset.previousChecked === 'true');
      if (previousIndex !== -1 && previousIndex !== currentIndex) indices.push(previousIndex);
      // Clear ghost data-current-checked on any non-checked inputs
      for (const radio of radios) {
        if (!radio.checked) radio.dataset.currentChecked = 'false';
      }
      return indices;
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.#clearValidation(event.target);
    this.#updateStockIndicator(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    // Close dropdown details panel after selection and update the displayed value
    const details = event.target.closest?.('details.variant-option__dropdown-details');
    if (details) {
      details.open = false;
      const display = details.querySelector('.variant-option__dropdown-selected');
      if (display && event.target instanceof HTMLInputElement) {
        display.textContent = event.target.value;
      }
    }

    // Out-of-stock variant: dispatch notification event and skip section fetch
    if (selectedOption.dataset.oosAction) {
      this.dispatchEvent(
        new CustomEvent('variant:oos-clicked', {
          bubbles: true,
          detail: {
            productId: this.dataset.productId,
            optionValueId: selectedOption.dataset.optionValueId,
            variantId: selectedOption.dataset.variantId,
            value: selectedOption.value,
          },
        })
      );
      return;
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Computes the stock status for a selected radio input.
   * @param {HTMLInputElement} radio - The selected radio input.
   * @returns {{ status: string, label: string }}
   */
  #computeStockStatus(radio) {
    const available = radio.dataset.optionAvailable === 'true';
    const management = radio.dataset.inventoryManagement || '';
    const quantity = Number.parseInt(radio.dataset.inventoryQuantity || '', 10);
    const policy = radio.dataset.inventoryPolicy || '';
    // No fallback: a missing/blank threshold means no division-resolved
    // threshold, so quantity <= NaN is always false and low stock never fires.
    const threshold = Number.parseInt(this.dataset.lowStockThreshold, 10);

    if (!available) {
      return { status: 'sold_out', label: this.dataset.labelSoldOut || 'Sold out' };
    }

    if (management === 'shopify' && !Number.isNaN(quantity)) {
      if (quantity <= 0 && policy === 'continue') {
        return { status: 'in_stock', label: this.dataset.labelInStock || 'In stock' };
      }
      if (quantity > 0 && quantity <= threshold) {
        return { status: 'low_stock', label: this.dataset.labelLowStock || 'Low stock' };
      }
    }

    return { status: 'in_stock', label: this.dataset.labelInStock || 'In stock' };
  }

  /**
   * Updates the stock indicator in the fieldset header for the given radio.
   * Only shown when all fieldsets have a selection, and only on the last fieldset header.
   * @param {HTMLElement} target - The selected input element.
   */
  #updateStockIndicator(target) {
    if (!(target instanceof HTMLInputElement)) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Only show when all fieldsets are selected.
    if (this.getFirstUnselectedFieldset() !== null) return;

    const lastFieldset = fieldsets[fieldsets.length - 1];
    if (!lastFieldset) return;

    const headerLabels = /** @type {HTMLElement[]} */ (this.refs.headerLabels || []);
    const lastHeaderLabel = headerLabels[fieldsets.length - 1];
    if (!lastHeaderLabel) return;

    const lastChecked = /** @type {HTMLInputElement | null} */ (lastFieldset.querySelector('input:checked'));
    if (!lastChecked) return;

    const { status, label } = this.#computeStockStatus(lastChecked);
    if (status === 'sold_out' && this.hasAttribute('data-coming-soon')) {
      lastHeaderLabel.innerHTML = '';
      return;
    }
    lastHeaderLabel.innerHTML =
      `<span class="variant-option__stock-indicator variant-option__stock-indicator--${status}">` +
      `<span class="variant-option__stock-dot" aria-hidden="true"></span>` +
      `<span class="variant-option__stock-label">${label}</span>` +
      `</span>`;
  }

  /**
   * Clears the validation message for the fieldset containing the target.
   * @param {HTMLElement} target - The selected input element.
   */
  #clearValidation(target) {
    const fieldset = target.closest?.('fieldset[data-fieldset-index]');
    if (!fieldset) return;

    const validation = fieldset.querySelector(':scope > .variant-option__validation');
    if (validation) {
      validation.setAttribute('hidden', '');
    }

    const details = fieldset.querySelector('details.variant-option__dropdown-details');
    if (details) {
      details.classList.remove('variant-option__dropdown-details--error');
    }
  }

  /** Disables all variant inputs and signals a loading state for assistive technology. */
  #disableInputs() {
    this.setAttribute('aria-busy', 'true');
    for (const fieldset of this.querySelectorAll('fieldset[data-fieldset-index]')) {
      fieldset.setAttribute('disabled', '');
    }
  }

  /** Re-enables all variant inputs and clears the loading state. */
  #enableInputs() {
    this.removeAttribute('aria-busy');
    for (const fieldset of this.querySelectorAll('fieldset[data-fieldset-index]')) {
      fieldset.removeAttribute('disabled');
    }
  }

  /**
   * Handles clicks on the dropdown backdrop overlay.
   * Closes the dropdown when the user clicks the fixed ::before pseudo-element
   * (which registers as a click on the details element itself).
   * @param {Event} event
   */
  #handleDropdownBackdropClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.matches('details.variant-option__dropdown-details[open]')) return;
    event.target.open = false;
  };

  /**
   * Positions the dropdown panel as a fixed overlay anchored below the trigger.
   * Locks body scroll on open, unlocks on close. Capture phase — toggle does not bubble.
   * @param {Event} event
   */
  #handleDropdownToggle = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.classList.contains('variant-option__dropdown-details')) return;
    const details = /** @type {HTMLDetailsElement} */ (event.target);
    if (details.open) {
      // Block dropdown opening while a variant fetch is in-flight
      if (this.hasAttribute('aria-busy')) {
        details.open = false;
        return;
      }
      const insideDialog = !!this.closest('dialog[open]');
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (!isMobile) {
        const summary = /** @type {HTMLElement | null} */ (details.querySelector('summary'));
        const panel = /** @type {HTMLElement | null} */ (details.querySelector('.variant-option__dropdown-panel'));
        if (panel && summary) {
          if (!insideDialog) {
            // Compensate for scrollbar removal to prevent layout shift.
            // Calculate BEFORE overflow:hidden — once the scrollbar is gone clientWidth changes.
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.documentElement.style.paddingRight = `${scrollbarWidth}px`;
            document.documentElement.style.overflow = 'hidden';
          }
          const triggerRect = summary.getBoundingClientRect();
          // When inside a dialog, position:fixed uses the dialog as containing block (due to transform).
          // Convert viewport coords to dialog-relative coords so the panel appears below the trigger.
          const dialog = this.closest('dialog');
          let panelTop = Math.max(0, triggerRect.bottom);
          let panelLeft = triggerRect.left;
          if (dialog) {
            const dialogRect = dialog.getBoundingClientRect();
            panelTop = Math.max(0, triggerRect.bottom - dialogRect.top);
            const productDetails = this.closest('.product-details');
            const ref = productDetails ?? dialog;
            const refRect = ref.getBoundingClientRect();
            panelLeft = triggerRect.left - refRect.left + (productDetails?.scrollLeft ?? 0);
          }
          panel.style.top = `${panelTop}px`;
          panel.style.left = `${panelLeft}px`;
          panel.style.width = `${triggerRect.width}px`;
          // Prefer viewport space below the trigger so the list can extend past short dialogs when overflow allows.
          const availableHeight = window.innerHeight - triggerRect.bottom - 8;
          if (availableHeight < 300) {
            panel.style.maxHeight = `${Math.max(100, availableHeight)}px`;
          }
        } else if (!insideDialog) {
          document.documentElement.style.overflow = 'hidden';
        }
      } else if (!insideDialog) {
        // Bottom-sheet mode — CSS handles positioning; only lock scroll
        document.documentElement.style.overflow = 'hidden';
      }
    } else {
      // Reset fixed position and release scroll lock
      const panel = /** @type {HTMLElement | null} */ (event.target.querySelector('.variant-option__dropdown-panel'));
      if (panel) {
        panel.style.top = '';
        panel.style.left = '';
        panel.style.width = '';
        panel.style.maxHeight = '';
      }
      // Only clear scroll lock if we're not inside a dialog — the dialog manages its own lock.
      if (!this.closest('dialog[open]')) {
        document.documentElement.style.overflow = '';
        document.documentElement.style.paddingRight = '';
      }
    }
  };

  /**
   * Restores the client-side selection state after a morph overwrites the DOM.
   * Re-applies checked radios, data attributes, stock indicators, and dropdown trigger text.
   * Uses option value IDs for robust radio lookup when indices may have shifted.
   * @param {number[][]} savedCheckedIndices - The checked indices from before morph.
   * @param {{ current: string | undefined, previous: string | undefined }[]} [savedOptionValueIds] - Option value IDs for fallback lookup.
   */
  #restoreSelectionState(savedCheckedIndices, savedOptionValueIds = []) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'))
    );

    if (fieldsets.length !== savedCheckedIndices.length) {
      return;
    }

    this.#radios = fieldsets.map((fs) => Array.from(fs.querySelectorAll('input')));
    this.#checkedIndices = savedCheckedIndices;

    for (let i = 0; i < savedCheckedIndices.length; i++) {
      const radios = this.#radios[i];
      const ids = savedOptionValueIds[i];
      let [currentIndex, previousIndex] = savedCheckedIndices[i];
      if (!radios) continue;

      // Resolve current selection by option value ID when index doesn't match
      if (ids?.current && currentIndex !== undefined) {
        if (!radios[currentIndex] || radios[currentIndex].dataset.optionValueId !== ids.current) {
          const found = radios.findIndex((r) => r.dataset.optionValueId === ids.current);
          if (found !== -1) {
            currentIndex = found;
            this.#checkedIndices[i][0] = found;
          }
        }
      }

      // Resolve previous selection by option value ID when index doesn't match
      if (ids?.previous && previousIndex !== undefined) {
        if (!radios[previousIndex] || radios[previousIndex].dataset.optionValueId !== ids.previous) {
          const found = radios.findIndex((r) => r.dataset.optionValueId === ids.previous);
          if (found !== -1) {
            previousIndex = found;
            if (this.#checkedIndices[i].length > 1) this.#checkedIndices[i][1] = found;
          }
        }
      }

      // Clear stale data attributes on ALL radios to prevent ghost selections
      for (const radio of radios) {
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }

      // Apply current selection
      if (currentIndex !== undefined && radios[currentIndex]) {
        const radio = radios[currentIndex];
        radio.checked = true;
        radio.dataset.currentChecked = 'true';

        // Restore dropdown trigger text
        const details = radio.closest('details.variant-option__dropdown-details');
        if (details) {
          const display = details.querySelector('.variant-option__dropdown-selected');
          if (display) display.textContent = radio.value;
        }
      }

      // Apply previous selection
      if (previousIndex !== undefined && radios[previousIndex]) {
        radios[previousIndex].dataset.previousChecked = 'true';
      }
    }

    // Apply stock indicator to last fieldset only when all fieldsets are selected.
    const lastFieldsetIndex = fieldsets.length - 1;
    const lastCheckedIdx = this.#checkedIndices[lastFieldsetIndex]?.[0];
    const lastRadio = lastCheckedIdx !== undefined ? this.#radios[lastFieldsetIndex]?.[lastCheckedIdx] : null;
    if (lastRadio) this.#updateStockIndicator(lastRadio);
  }

  /**
   * Returns the first unselected fieldset element, or null if all are selected.
   * @returns {HTMLFieldSetElement | null}
   */
  getFirstUnselectedFieldset() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    for (const fieldset of fieldsets) {
      const hasChecked = fieldset.querySelector('input:checked');
      if (!hasChecked) return fieldset;
    }
    return null;
  }

  /**
   * Returns all unselected fieldset elements.
   * @returns {HTMLFieldSetElement[]}
   */
  getAllUnselectedFieldsets() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    return fieldsets.filter(fieldset => !fieldset.querySelector('input:checked'));
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();

    const generation = ++this.#fetchGeneration;
    this.#disableInputs();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        // Close any open dropdowns before morph. The morph preserves <details>.open but
        // strips inline panel positioning styles, sending the panel to top:-9999px while
        // the backdrop and scroll lock remain stuck. Closing first fires the toggle event
        // synchronously, which lets #handleDropdownToggle clear both cleanly.
        for (const details of this.querySelectorAll('details.variant-option__dropdown-details[open]')) {
          /** @type {HTMLDetailsElement} */ (details).open = false;
        }

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      })
      .finally(() => {
        if (generation === this.#fetchGeneration) {
          this.#enableInputs();
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Sync stale #radios/#checkedIndices if sibling-products section morph updated the DOM
    // without triggering updatedCallback (childrenOnly morph does not recurse into nested
    // Component instances). A count mismatch means the current product has a different number
    // of option groups than the product that was active when connectedCallback last ran.
    const liveFieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));
    if (liveFieldsets.length !== this.#radios.length) {
      this.#radios = liveFieldsets.map((fs) => Array.from(fs.querySelectorAll('input')));
      this.#checkedIndices = this.#radios.map((radios) => {
        const idx = radios.findIndex((r) => r.checked);
        return idx !== -1 ? [idx] : [];
      });
    }

    // Save selection state before morph — server HTML has pre-selection removed,
    // which would otherwise uncheck radios and break the variant update flow.
    // Save both indices and stable option value IDs for robust restoration.
    const savedCheckedIndices = this.#checkedIndices.map((arr) => [...arr]);
    const savedOptionValueIds = this.#radios.map((radios, i) => {
      const [currentIdx, previousIdx] = this.#checkedIndices[i] ?? [];
      return {
        current: currentIdx !== undefined ? radios[currentIdx]?.dataset.optionValueId : undefined,
        previous: previousIdx !== undefined ? radios[previousIdx]?.dataset.optionValueId : undefined,
      };
    });

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    this.#restoreSelectionState(savedCheckedIndices, savedOptionValueIds);
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset[data-fieldset-index] input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset[data-fieldset-index] input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
